import type { ThreadMessageLike } from "@assistant-ui/react";
import { shallowEqual } from "react-redux";

import { choiceActivity, choiceFields } from "../protocol/choice";
import type {
  ChoiceKind,
  ToolUiKind,
  WireChoice,
  WireChoiceDisplay,
  WirePart,
} from "../protocol/protocol";
import type { LivePart } from "../store/live";
import { resultCard } from "../results";
import {
  cellPartKey,
  type ChartPart,
  type PreviewPart,
  type ToolView,
  type ViewMessage,
} from "../store/selectors";
import { cellStep, sameStep, stepLine, toolStep, type Step } from "./steps";
import { toolLine } from "./toolLines";

/** The approval view; a tool part carrying it renders in our sandboxed frame (design 5.1). */
export const APPROVAL_VIEW_URI = "ui://steddy/approval";

/** What our tool renderer reads off a tool-call part, beside assistant-ui's own fields. */
export interface ToolArtifact {
  line: string;
  state: ToolView["state"];
  choiceRef: string | null;
  /** Picks which card renders the call; a filing (or no choice) is a plain line. */
  choiceKind: ChoiceKind | null;
  /** The choice's display hints (activity, the key/value grid), for the pick card. */
  choiceDisplay: WireChoiceDisplay | null;
  /** The result shows as a card under the line (a read the user asked to see; `results/`). */
  showsResult: boolean;
  /** The pages a result cites (a web search's), https only, as chips under its line. */
  sources: ToolSource[];
  /** The tool's ToolUi kind (stored calls; null live): picks a native card (`NATIVE_CARDS`). */
  uiKind: ToolUiKind | null;
  /** The tool_result's `card` (what the call did, `Tool.result_card`); null until it ran. */
  resultCard: unknown;
  /**
   * The `kind` of the choice's own card (`Tool.approval_card`), which the card reads from the
   * store by `choiceRef`; null without one. A string, so a choice update keeps the part.
   */
  choiceCardKind: string | null;
  /** That card's `version`: a card drawn natively only at a version it knows. */
  choiceCardVersion: number | null;
  /** A chart the call showed: the chart card renders instead of the line. */
  chart: ChartPart | null;
  /** A sandbox call's card under the line (its output, or its cell); null for any other call. */
  step: Step | null;
  /** A workspace page the call shows: it renders in the preview frame, after the call's line. */
  preview: PreviewPart | null;
}

export interface ToolSource {
  title: string;
  url: string;
}

const MAX_SOURCES = 5;

/** A result's `sources` ([{title, url}]), kept only as https links, each once, at most five. */
export function toolSources(content: unknown): ToolSource[] {
  const listed = (content as { sources?: unknown } | null)?.sources;
  if (!Array.isArray(listed)) return [];
  const sources: ToolSource[] = [];
  for (const item of listed) {
    const { title, url } = (item ?? {}) as { title?: unknown; url?: unknown };
    if (typeof url !== "string" || sources.some((source) => source.url === url)) continue;
    try {
      if (new URL(url).protocol !== "https:") continue;
    } catch {
      continue;
    }
    sources.push({ title: typeof title === "string" ? title : "", url });
    if (sources.length === MAX_SOURCES) break;
  }
  return sources;
}

type Content = Exclude<ThreadMessageLike["content"], string>;
type Part = Content[number];
type ToolCallPart = Extract<Part, { type: "tool-call" }>;

function cardKind(card: unknown): string | null {
  const kind = (card as { kind?: unknown } | null)?.kind;
  return typeof kind === "string" ? kind : null;
}

function cardVersion(card: unknown): number | null {
  const version = (card as { version?: unknown } | null)?.version;
  return typeof version === "number" ? version : null;
}

/** `approvals`: how many of a code cell's approvals show after it (`cellApprovalParts`). */
function toolCallPart(
  id: string,
  name: string,
  args: Record<string, unknown>,
  view: ToolView | undefined,
  uiKind: ToolUiKind | null,
  approvals = 0
): ToolCallPart {
  const state = view?.state ?? "running";
  const choice = view?.choice ?? null;
  const step = toolStep(name, args, view?.result ?? null, approvals);
  const artifact: ToolArtifact = {
    line: stepLine(toolLine(state, view?.activity ?? null, choice?.status === "pending"), step),
    state,
    choiceRef: choice?.ref ?? null,
    choiceKind: choice?.kind ?? null,
    choiceDisplay: choice
      ? { activity: choiceActivity(choice) ?? undefined, fields: choiceFields(choice) }
      : null,
    sources: toolSources(view?.result?.content),
    showsResult: resultCard(name, view?.result?.content) !== null,
    uiKind,
    chart: view?.chart ?? null,
    resultCard: view?.result?.card ?? null,
    choiceCardKind: cardKind(choice?.card),
    choiceCardVersion: cardVersion(choice?.card),
    step,
    preview: view?.preview ?? null,
  };
  return {
    type: "tool-call",
    toolCallId: id,
    toolName: name, // internal: our renderer shows artifact.line, never this
    args: args as ToolCallPart["args"],
    artifact,
    ...(view?.result && {
      result: view.result.content,
      isError: view.result.outcome === "failed" || view.result.outcome === "denied",
    }),
    ...(choice?.kind === "approval" && { mcp: { app: { resourceUri: APPROVAL_VIEW_URI } } }),
  };
}

// A choice arrives as a new object with every store update, so its hints compare by content.
function sameDisplay(a: WireChoiceDisplay | null, b: WireChoiceDisplay | null): boolean {
  if (!a || !b) return a === b;
  const [x, y] = [a.fields ?? [], b.fields ?? []];
  return (
    shallowEqual(a.activity, b.activity) &&
    x.length === y.length &&
    x.every((field, i) => shallowEqual(field, y[i]))
  );
}

function sameToolPart(a: ToolCallPart, b: ToolCallPart): boolean {
  const [x, y] = [a.artifact as ToolArtifact, b.artifact as ToolArtifact];
  return (
    x.line === y.line &&
    x.state === y.state &&
    x.choiceRef === y.choiceRef &&
    x.choiceKind === y.choiceKind &&
    x.uiKind === y.uiKind &&
    x.chart === y.chart &&
    x.showsResult === y.showsResult &&
    x.resultCard === y.resultCard &&
    x.choiceCardKind === y.choiceCardKind &&
    x.choiceCardVersion === y.choiceCardVersion &&
    sameDisplay(x.choiceDisplay, y.choiceDisplay) &&
    sameStep(x.step, y.step) &&
    x.preview === y.preview &&
    a.result === b.result &&
    a.isError === b.isError
  );
}

// A stored call's part is rebuilt only when what it shows changes. A choice's status alone does
// not change it: the card reads its choice from the store by ref, so its frame never remounts.
const toolParts = new WeakMap<object, ToolCallPart>();
function storedToolPart(source: object, built: ToolCallPart): ToolCallPart {
  const cached = toolParts.get(source);
  if (cached && sameToolPart(cached, built)) return cached;
  toolParts.set(source, built);
  return built;
}

// Stored parts never change, so each converts once; assistant-ui then sees the same part objects.
const converted = new WeakMap<object, Part>();
function memo(source: object, build: () => Part): Part {
  let part = converted.get(source);
  if (!part) {
    part = build();
    converted.set(source, part);
  }
  return part;
}

function textPart(part: WirePart | LivePart, kind: "text" | "reasoning", text: string): Part {
  return memo(part, () => ({ type: kind, text }));
}

/**
 * A code cell the assistant ran, as a done call with its card: it renders and groups like one.
 * Its line is its summary, which counts the approvals that show (`approvals`), so it is rebuilt
 * when they change.
 */
function cellPart(
  id: string,
  part: Extract<WirePart, { kind: "sandbox_cell" }>,
  approvals: number
): ToolCallPart | null {
  const step = cellStep(part, approvals);
  if (step?.kind !== "cell") return null;
  return storedToolPart(part, {
    type: "tool-call",
    toolCallId: id,
    toolName: "sandbox_cell",
    args: {},
    artifact: {
      line: step.cell.summary || "Ran code",
      state: "success",
      choiceRef: null,
      choiceKind: null,
      choiceDisplay: null,
      showsResult: false,
      uiKind: null,
      resultCard: null,
      choiceCardKind: null,
      chart: null,
      choiceCardVersion: null,
      sources: [],
      step,
      preview: null,
    } satisfies ToolArtifact,
  });
}

// The approval parts of each cell's call or part, by ref: built once, as stored parts are.
const cellCards = new WeakMap<object, Map<string, ToolCallPart>>();

/**
 * The cards of the approvals a code cell asked for (`cellChoices`), right after it: a card, so it
 * stays out of the folded steps. Each reads its choice by ref.
 */
function cellApprovalParts(source: object, choices: WireChoice[] = []): ToolCallPart[] {
  if (!choices.length) return [];
  let cards = cellCards.get(source);
  if (!cards) cellCards.set(source, (cards = new Map()));
  return choices.map((choice) => {
    let card = cards.get(choice.ref);
    if (!card) {
      const view = {
        state: "running",
        activity: null,
        result: null,
        choice,
        chart: null,
        preview: null,
      } as const;
      card = toolCallPart(`approval:${choice.ref}`, "approval", {}, view, null);
      cards.set(choice.ref, card);
    }
    return card;
  });
}

type Attachment = NonNullable<ThreadMessageLike["attachments"]>[number];

function attachment(id: number, name: string, contentType: string): Attachment {
  return {
    id: `tf:${id}`,
    type: contentType.startsWith("image/") ? "image" : "document",
    name,
    contentType,
    status: { type: "complete" },
    content: [],
  };
}

/** A voice note on a user message, as UserMessage draws it (`metadata.custom.voice`). */
export interface VoiceNoteMeta {
  id: number;
  seconds: number;
  transcript: string;
}

function storedParts(view: Extract<ViewMessage, { type: "stored" }>): {
  content: Part[];
  attachments: Attachment[];
  voice: VoiceNoteMeta[];
} {
  const content: Part[] = [];
  const attachments: Attachment[] = [];
  const voice: VoiceNoteMeta[] = [];
  for (const [index, part] of view.message.parts.entries()) {
    if (part.kind === "text" || part.kind === "reasoning") {
      content.push(textPart(part, part.kind, part.text));
    } else if (part.kind === "tool_call") {
      const asked = view.cellChoices[part.id] ?? [];
      const call = storedToolPart(
        part,
        toolCallPart(
          part.id,
          part.name,
          part.args,
          view.tools[part.id],
          part.ui_kind ?? null,
          asked.length
        )
      );
      content.push(call, ...cellApprovalParts(part, asked));
    } else if (part.kind === "sandbox_cell") {
      const asked = view.cellChoices[cellPartKey(index)] ?? [];
      const cell = cellPart(`cell:${view.message.id}:${index}`, part, asked.length);
      if (cell) content.push(cell, ...cellApprovalParts(part, asked));
    } else if (part.kind === "file" && part.thread_file_id != null) {
      attachments.push(attachment(part.thread_file_id, part.name, part.media_type));
    } else if (part.kind === "voice") {
      voice.push({ id: part.thread_file_id, seconds: part.seconds, transcript: part.transcript });
    }
  }
  return { content, attachments, voice };
}

/** Unique across threads: the runtime's repository and React keys use it. */
function viewId(view: ViewMessage): string {
  if (view.type === "stored") return `m:${view.message.id}`;
  return view.type === "live" ? view.id : `out:${view.message.cid}`;
}

const RUNNING = { type: "running" } as const;
const COMPLETE = { type: "complete", reason: "unknown" } as const;
const NEEDS_CHOICE = { type: "requires-action", reason: "tool-calls" } as const;

const CONVERTERS: {
  [K in ViewMessage["type"]]: (view: Extract<ViewMessage, { type: K }>) => ThreadMessageLike;
} = {
  stored(view) {
    if (view.message.role === "event" || view.message.role === "boundary") {
      // The server's line: one text part, shown muted (EventMessage), never as anyone's bubble; a
      // boundary (the user cleared the conversation) is drawn as a divider instead.
      const text = view.message.parts
        .map((part) => (part.kind === "text" ? part.text : ""))
        .join(" ")
        .trim();
      return {
        id: viewId(view),
        role: "system",
        createdAt: new Date(view.message.created_at),
        content: [{ type: "text", text }],
        metadata: { custom: { boundary: view.message.role === "boundary" } },
      };
    }
    const { content, attachments, voice } = storedParts(view);
    const waiting =
      Object.values(view.tools).some((tool) => tool.choice?.status === "pending") ||
      Object.values(view.cellChoices).some((asked) =>
        asked.some((choice) => choice.status === "pending")
      );
    return {
      id: viewId(view),
      role: view.message.role === "user" ? "user" : "assistant",
      createdAt: new Date(view.message.created_at),
      content,
      ...(attachments.length && { attachments }),
      ...(view.message.role !== "user" && { status: waiting ? NEEDS_CHOICE : COMPLETE }),
      metadata: {
        custom: {
          seq: view.message.seq,
          pending: view.pending,
          model: view.message.model ?? null,
          ...(voice.length && { voice }),
        },
      },
    };
  },
  live(view) {
    return {
      id: viewId(view),
      role: "assistant",
      status: RUNNING,
      content: [
        ...view.parts.map((part) => textPart(part, part.kind, part.text)),
        ...view.toolCallIds.map((id) => toolCallPart(id, "tool", {}, view.tools[id], null)),
      ],
      // A late join or a missed delta: the UI shows the typing indicator until the step lands.
      metadata: { custom: { gap: view.gap } },
    };
  },
  outgoing(view) {
    const { message } = view;
    return {
      id: viewId(view),
      role: "user",
      content: message.text ? [{ type: "text", text: message.text }] : [],
      ...(message.files.length && {
        attachments: message.files.map((file) => attachment(file.id, file.name, file.contentType)),
      }),
      metadata: {
        custom: {
          state: message.state,
          error: message.error?.message ?? null,
          errorCode: message.error?.code ?? null,
        },
      },
    };
  },
};

/**
 * Our view messages as assistant-ui messages. Module-level so its identity never changes (a new
 * converter flushes assistant-ui's whole cache), and it never throws: a bad message renders empty.
 */
export function convertMessage(view: ViewMessage): ThreadMessageLike {
  try {
    return (CONVERTERS[view.type] as (view: ViewMessage) => ThreadMessageLike)(view);
  } catch {
    return { id: viewId(view), role: "assistant", content: [], status: COMPLETE };
  }
}
